import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { mdiMicrophone, mdiStop, mdiDelete } from '@mdi/js'
import { Button } from '../ui/button'
import { cn } from '../../lib/utils'

type Props = {
    /** The recording, or null when there is none yet. Owned by the caller so a
     * dialog reset clears it. */
    value: Blob | null
    onChange: (blob: Blob | null) => void
    disabled?: boolean
    /** Called as soon as a recording lands AND actually contains speech, so the
     * host can transcribe it while the declarer is still on the page. Silent or
     * too-short recordings are kept but never sent — see PEAK_THRESHOLD. */
    onRecorded?: (blob: Blob) => void
    /** Called instead of `onRecorded` when the recording held no speech, so the
     * host can say so rather than leaving a spinner that never resolves. */
    onNoSpeech?: () => void
    /** True while the host is transcribing — the button says so rather than
     * looking idle. */
    transcribing?: boolean
    /** Prefix of every `data-testid` this component renders. The declaration
     * dialog keeps `voice-note`; the resident composer passes `resident`, so
     * its microphone is `resident-record`. */
    testIdPrefix?: string
    /** Icon-only, inline with a composer's other buttons, instead of the
     * full-width button a form wants. */
    compact?: boolean
    /** Told when a recording starts and stops, so a host that swaps this
     * button for another one (the resident composer's send) keeps it mounted
     * until the take is over. */
    onRecordingChange?: (recording: boolean) => void
    /** Extra classes on the compact record button (the composer's round one). */
    buttonClassName?: string
    /** Variant of the record button while idle. Recording is always
     * `destructive`. */
    idleVariant?: 'outline' | 'default'
}

/** Longest recording we keep. A voice note is a sentence, not a statement; the
 * cap keeps a forgotten-running microphone from uploading ten megabytes. */
const MAX_SECONDS = 120

/**
 * Below these, the recording is not sent for transcription.
 *
 * This gate is not an optimisation, it is a safety device. Fed audio with no
 * speech in it, the transcription model does not answer "nothing here" — it
 * invents something fluent and plausible (measured: two seconds of a test tone
 * came back as an entirely fabricated French declaration about a leak). A
 * resident who taps record by accident in a quiet hall must never end up with a
 * ticket describing a problem nobody reported, so audio that never crossed a
 * speech level, or that is too short to hold a sentence, never reaches the API.
 *
 * PEAK_THRESHOLD is on the [0,1] amplitude of the time-domain signal. Room tone
 * and handling noise sit well under 0.05; speech at arm's length peaks far
 * above it.
 */
const PEAK_THRESHOLD = 0.05
const MIN_SECONDS = 1

/**
 * Whether this browser can record at all.
 *
 * Probed once, at module scope, because the answer cannot change during a
 * session: an insecure origin, an iOS browser without `MediaRecorder`, a
 * desktop with no input device all fail the same way. Callers HIDE the
 * microphone when it answers false rather than disabling it — a disabled
 * button on a phone is a question the resident cannot answer.
 */
export function isRecordingSupported(): boolean {
    return (
        typeof MediaRecorder !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        Boolean(navigator.mediaDevices?.getUserMedia)
    )
}

function pickMimeType(): string {
    // Chrome/Firefox/Android give opus in webm; Safari only offers mp4/aac.
    for (const candidate of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(candidate)) {
            return candidate
        }
    }
    return ''
}

/**
 * Record a short spoken note.
 *
 * The point is the declarer who will not type: a caretaker with gloves on, a
 * resident who would rather say "ça fuit depuis le radiateur du fond, sous la
 * fenêtre" than write it. It records, it plays back, it deletes — nothing else,
 * because anything else is a field to think about.
 */
export function VoiceNoteRecorder({
    value,
    onChange,
    disabled = false,
    onRecorded,
    onNoSpeech,
    transcribing = false,
    testIdPrefix = 'voice-note',
    compact = false,
    onRecordingChange,
    buttonClassName,
    idleVariant = 'outline',
}: Props) {
    const { t } = useTranslation('assets')
    const [recording, setRecording] = useState(false)
    const [seconds, setSeconds] = useState(0)
    const [error, setError] = useState<string | null>(null)
    const recorderRef = useRef<MediaRecorder | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    // Loudest sample seen while recording, and the graph watching for it.
    // Measured live off the stream rather than by decoding the blob afterwards:
    // decodeAudioData does not handle opus everywhere, and the meter has to
    // work whatever container we recorded into.
    const peakRef = useRef(0)
    const elapsedRef = useRef(0)
    const audioCtxRef = useRef<AudioContext | null>(null)
    const meterRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)

    // The cleanup also runs on unmount: a host that moves this component
    // once a take lands must still hear that the recording is over.
    useEffect(() => {
        if (!recording || !onRecordingChange) return
        onRecordingChange(true)
        return () => onRecordingChange(false)
    }, [recording, onRecordingChange])

    // An object URL outlives the blob it was minted from unless it is revoked,
    // and this component is remounted on every dialog open.
    useEffect(() => {
        if (!value) {
            setPreviewUrl(null)
            return
        }
        const url = URL.createObjectURL(value)
        setPreviewUrl(url)
        return () => URL.revokeObjectURL(url)
    }, [value])

    const stop = useCallback(() => {
        recorderRef.current?.stop()
    }, [])

    useEffect(() => {
        // Releasing the microphone on unmount matters: the browser keeps the
        // recording indicator lit otherwise, which reads as the app listening.
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
            if (meterRef.current) clearInterval(meterRef.current)
            void audioCtxRef.current?.close().catch(() => { /* already closed */ })
            const recorder = recorderRef.current
            if (recorder && recorder.state !== 'inactive') recorder.stop()
            recorder?.stream.getTracks().forEach((track) => track.stop())
        }
    }, [])

    const start = useCallback(async () => {
        setError(null)
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            const mimeType = pickMimeType()
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined)
            recorderRef.current = recorder
            chunksRef.current = []

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunksRef.current.push(e.data)
            }
            recorder.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })
                const kept = blob.size > 0 ? blob : null
                const heardSpeech = peakRef.current >= PEAK_THRESHOLD && elapsedRef.current >= MIN_SECONDS
                onChange(kept)
                // Transcribe immediately: the declarer is still here, and they
                // are the only one who can correct what the model misheard.
                // Silence never goes — the model invents words to fill it.
                if (kept && heardSpeech) onRecorded?.(kept)
                else if (kept) onNoSpeech?.()
                setRecording(false)
                if (timerRef.current) clearInterval(timerRef.current)
                if (meterRef.current) clearInterval(meterRef.current)
                void audioCtxRef.current?.close().catch(() => { /* already closed */ })
                audioCtxRef.current = null
                stream.getTracks().forEach((track) => track.stop())
                recorderRef.current = null
            }

            // Peak meter on the live stream, sampled ten times a second.
            peakRef.current = 0
            elapsedRef.current = 0
            try {
                const audioCtx = new AudioContext()
                audioCtxRef.current = audioCtx
                const analyser = audioCtx.createAnalyser()
                analyser.fftSize = 2048
                audioCtx.createMediaStreamSource(stream).connect(analyser)
                const samples = new Float32Array(analyser.fftSize)
                meterRef.current = setInterval(() => {
                    analyser.getFloatTimeDomainData(samples)
                    for (const sample of samples) {
                        const amplitude = Math.abs(sample)
                        if (amplitude > peakRef.current) peakRef.current = amplitude
                    }
                }, 100)
            } catch (meterError) {
                // No meter means no gate; treat the recording as speech rather
                // than silently refusing to transcribe a real one.
                console.warn('Voice note level meter unavailable:', meterError)
                peakRef.current = 1
            }

            recorder.start()
            setRecording(true)
            setSeconds(0)
            timerRef.current = setInterval(() => {
                setSeconds((s) => {
                    const next = s + 1
                    elapsedRef.current = next
                    if (next >= MAX_SECONDS) stop()
                    return next
                })
            }, 1000)
        } catch (e) {
            // Denied permission, no microphone, or an insecure origin. Say so
            // rather than leaving a button that does nothing.
            console.error('Voice note recording failed to start:', e)
            setError(t('declare.voice_error', {
                defaultValue: "Micro indisponible — vérifiez l'autorisation du navigateur.",
            }))
        }
    }, [onChange, onRecorded, onNoSpeech, stop, t])

    return (
        <div data-testid={`${testIdPrefix}-recorder`}>
            {value && previewUrl ? (
                <div className="flex items-center gap-2">
                    <audio controls src={previewUrl} className="h-10 min-w-0 flex-1" data-testid={`${testIdPrefix}-playback`} />
                    {transcribing && (
                        <span className="text-xs text-muted-foreground" data-testid={`${testIdPrefix}-transcribing`}>
                            {t('declare.transcribing')}
                        </span>
                    )}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onChange(null)}
                        disabled={disabled || transcribing}
                        aria-label={t('declare.voice_delete', { defaultValue: 'Supprimer la note vocale' })}
                        title={t('declare.voice_delete', { defaultValue: 'Supprimer la note vocale' })}
                        data-testid={`${testIdPrefix}-delete`}
                    >
                        <AppIcon path={mdiDelete} size="md" />
                    </Button>
                </div>
            ) : (
                <Button
                    type="button"
                    variant={recording ? 'destructive' : idleVariant}
                    onClick={recording ? stop : start}
                    disabled={disabled}
                    size={compact ? 'icon' : 'default'}
                    className={cn(compact ? 'h-11 w-11 flex-shrink-0' : 'h-11 w-full', buttonClassName)}
                    aria-label={recording ? t('declare.voice_stop_short', { defaultValue: "Arrêter l'enregistrement" }) : t('declare.voice_record', { defaultValue: 'Enregistrer une note vocale' })}
                    title={recording ? t('declare.voice_stop_short', { defaultValue: "Arrêter l'enregistrement" }) : t('declare.voice_record', { defaultValue: 'Enregistrer une note vocale' })}
                    data-testid={recording ? `${testIdPrefix}-stop` : `${testIdPrefix}-record`}
                >
                    <AppIcon path={recording ? mdiStop : mdiMicrophone} size="md" className={compact ? '' : 'mr-2'} />
                    {compact
                        ? recording && <span className="sr-only">{String(seconds).padStart(2, '0')}</span>
                        : recording
                            ? t('declare.voice_stop', {
                                seconds: String(seconds).padStart(2, '0'),
                                defaultValue: 'Arrêter ({{seconds}} s)',
                            })
                            : t('declare.voice_record', { defaultValue: 'Enregistrer une note vocale' })}
                </Button>
            )}
            {error && <p className="mt-1 text-xs text-destructive" data-testid={`${testIdPrefix}-error`}>{error}</p>}
        </div>
    )
}
